import { createContext, useContext, useState } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';

import authAPI, { AuthResponse } from 'app/auth';
import { fetchUserInfo } from 'app/store/user';
import { useAppDispatch } from 'app/store';
import { fetchClientIp } from '../app/store/clientIp';
import { User } from 'firebase/auth';

type AuthProviderState = {
  user: User | null | undefined;
  loading: boolean;
  error: Error | string | undefined;
  logout: () => void;
  signInOrRegisterWithGoogle: (website?: string | undefined) => Promise<boolean>;
  logInWithEmailAndPassword: (email: string, password: string) => Promise<boolean>;
  registerWithEmailAndPassword: (
    firstName: string,
    lastName: string,
    email: string,
    website: string,
    password: string,
  ) => Promise<User | null>;
  sendPasswordReset: (email: string) => Promise<boolean>;
  sendVerificationEmail: (targetUser?: User | null) => Promise<AuthResponse>;
  reloadUser: (targetUser?: User | null) => Promise<AuthResponse<User>>;
  assumeDeveloperIdentity: (
    developerId: string,
    apiKey: string | undefined,
    userToken: string | undefined | null,
    readOnly?: boolean,
  ) => Promise<boolean>;
};

const initialState: AuthProviderState = {
  user: null,
  loading: false,
  error: undefined,
  logout: () => null,
  signInOrRegisterWithGoogle: () => Promise.resolve(false),
  logInWithEmailAndPassword: () => Promise.resolve(false),
  registerWithEmailAndPassword: () => Promise.resolve(null),
  sendPasswordReset: () => Promise.resolve(false),
  sendVerificationEmail: () => Promise.resolve({ ok: false, error: 'No signed-in user found.' }),
  reloadUser: () => Promise.resolve({ ok: false, error: 'No signed-in user found.' }),
  assumeDeveloperIdentity: () => Promise.resolve(false),
};

const AuthContext = createContext<AuthProviderState>(initialState);

type AuthError = Error | string | undefined;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, loading, error] = useAuthState(authAPI.auth);
  const [authError, setAuthError] = useState<AuthError>(error);
  const dispatch = useAppDispatch();

  const handlePostLogin = async (
    authResult: AuthResponse<User>,
    website?: string,
  ): Promise<boolean> => {
    if (!authResult.ok) {
      setAuthErrorWithTimer(authResult.error);
      return false;
    }

    const user = authResult.data;
    dispatch(fetchClientIp());
    dispatch(fetchUserInfo(user.uid, user, website));

    return true;
  };

  const setAuthErrorWithTimer = (error: string) => {
    setAuthError(error);
    setTimeout(() => setAuthError(''), 1000); // reset state of error to trigger next error
  };

  // Register with Google account, or sign in if account already exists
  const signInOrRegisterWithGoogle = async (website?: string) => {
    const result = await authAPI.signInOrRegisterWithGoogle(website);
    return handlePostLogin(result, website);
  };

  const logInWithEmailAndPassword = async (email: string, password: string) => {
    const result = await authAPI.logInWithEmailAndPassword(email, password);
    return handlePostLogin(result);
  };

  const assumeDeveloperIdentity = async (
    developerId: string,
    apiKey: string | undefined,
    userToken: string | undefined | null,
    readOnly = true,
  ) => {
    const result = await authAPI.assumeDeveloperIdentity(developerId, apiKey, userToken, readOnly);
    return handlePostLogin(result);
  };

  const registerWithEmailAndPassword = async (
    firstName: string,
    lastName: string,
    email: string,
    website: string,
    password: string,
  ) => {
    const result = await authAPI.registerWithEmailAndPassword({
      firstName,
      lastName,
      website,
      email,
      password,
    });
    const didRegister = await handlePostLogin(result, website);

    if (!didRegister || !result.ok) {
      return null;
    }

    return result.data;
  };

  const sendPasswordReset = async (email: string) => {
    const { error } = await authAPI.sendPasswordReset(email);

    if (error) {
      setAuthErrorWithTimer(error);
      return false;
    } else {
      return true;
    }
  };

  const sendVerificationEmail = async (
    targetUser: User | null | undefined = user,
  ): Promise<AuthResponse> => {
    if (!targetUser) {
      return { ok: false, error: 'No signed-in user found.' };
    }

    return authAPI.sendVerificationEmail(targetUser);
  };

  const reloadUser = async (
    targetUser: User | null | undefined = user,
  ): Promise<AuthResponse<User>> => {
    if (!targetUser) {
      return { ok: false, error: 'No signed-in user found.' };
    }

    return authAPI.reloadUser(targetUser);
  };

  const logout = async () => {
    authAPI.logout();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error: authError,
        logout,
        signInOrRegisterWithGoogle,
        logInWithEmailAndPassword,
        registerWithEmailAndPassword,
        sendPasswordReset,
        sendVerificationEmail,
        reloadUser,
        assumeDeveloperIdentity,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
